"use client";

import { RefObject } from "react";
import {
    formatMonthLabel,
    toDateKey,
    parseDateKey,
    buildCalendarCells,
} from "./landing-nav-types";

type DateCalendarProps = {
    isDateMenuOpen: boolean;
    dateMenuAnchor: "desktop" | "mobile" | null;
    anchor: "desktop" | "mobile";
    dateValue: string;
    calendarCursor: { year: number; month: number };
    onCalendarCursorChange: (cursor: { year: number; month: number }) => void;
    onDateChange: (value: string) => void;
    onOpenDatePicker: (anchor: "desktop" | "mobile") => void;
    containerRef: RefObject<HTMLDivElement | null>;
};

const weekdayLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function DateCalendar({
    isDateMenuOpen,
    dateMenuAnchor,
    anchor,
    dateValue,
    calendarCursor,
    onCalendarCursorChange,
    onDateChange,
    onOpenDatePicker,
    containerRef,
}: DateCalendarProps) {
    const todayKey = toDateKey(new Date());
    const selectedDate = parseDateKey(dateValue);
    const calendarCells = buildCalendarCells(calendarCursor.year, calendarCursor.month);

    const renderDateMenu = () => {
        if (!isDateMenuOpen || dateMenuAnchor !== anchor) {
            return null;
        }
        const selectedKey = selectedDate ? toDateKey(selectedDate) : "";
        return (
            <div className="absolute right-0 top-full z-40 mt-2 w-[260px] max-w-[calc(100vw-2rem)] rounded-2xl border border-[var(--hopp-panel-strong)] bg-[var(--hopp-panel)] p-4 shadow-[0_20px_40px_rgba(4,8,20,0.55)]">
                <div className="flex items-center justify-between">
                    <button
                        type="button"
                        onClick={() =>
                            onCalendarCursorChange({
                                year: calendarCursor.month - 1 < 0 ? calendarCursor.year - 1 : calendarCursor.year,
                                month: calendarCursor.month - 1 < 0 ? 11 : calendarCursor.month - 1,
                            })
                        }
                        className="flex h-8 w-8 items-center justify-center rounded-full border border-[var(--hopp-panel-strong)] text-[var(--hopp-sapphire)] transition hover:border-[var(--hopp-sapphire)]"
                        aria-label="Previous month"
                    >
                        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M15 6l-6 6 6 6" />
                        </svg>
                    </button>
                    <p className="text-sm font-semibold text-[var(--hopp-text)]">
                        {formatMonthLabel(calendarCursor.year, calendarCursor.month)}
                    </p>
                    <button
                        type="button"
                        onClick={() =>
                            onCalendarCursorChange({
                                year: calendarCursor.month + 1 > 11 ? calendarCursor.year + 1 : calendarCursor.year,
                                month: calendarCursor.month + 1 > 11 ? 0 : calendarCursor.month + 1,
                            })
                        }
                        className="flex h-8 w-8 items-center justify-center rounded-full border border-[var(--hopp-panel-strong)] text-[var(--hopp-sapphire)] transition hover:border-[var(--hopp-sapphire)]"
                        aria-label="Next month"
                    >
                        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M9 6l6 6-6 6" />
                        </svg>
                    </button>
                </div>
                <div className="mt-3 grid grid-cols-7 gap-1 text-[10px] uppercase tracking-[0.2em] text-[var(--hopp-muted)]">
                    {weekdayLabels.map((label) => (
                        <span key={label} className="text-center">
                            {label}
                        </span>
                    ))}
                </div>
                <div className="mt-2 grid grid-cols-7 gap-1 text-xs">
                    {calendarCells.map((cell) => {
                        const key = toDateKey(cell.date);
                        const isSelected = key === selectedKey;
                        const isToday = key === todayKey;
                        const isPast = key < todayKey;
                        return (
                            <button
                                key={key}
                                type="button"
                                onClick={isPast ? undefined : () => onDateChange(key)}
                                className={`flex h-8 items-center justify-center rounded-lg transition ${
                                    isPast
                                        ? "opacity-30 pointer-events-none"
                                        : cell.inMonth
                                            ? "text-[var(--hopp-text)]"
                                            : "text-[var(--hopp-muted)]/60"
                                } ${
                                    !isPast && isSelected
                                        ? "bg-[var(--hopp-sapphire)] text-white shadow-[0_8px_18px_rgba(58,92,255,0.35)]"
                                        : !isPast
                                            ? "hover:bg-[var(--hopp-panel-strong)]"
                                            : ""
                                } ${
                                    !isPast && !isSelected && isToday
                                        ? "border border-[var(--hopp-sapphire)]/60"
                                        : ""
                                }`}
                            >
                                {cell.date.getUTCDate()}
                            </button>
                        );
                    })}
                </div>
                <div className="mt-3 flex items-center justify-between text-[10px] uppercase tracking-[0.2em] text-[var(--hopp-muted)]">
                    <button
                        type="button"
                        onClick={() => onDateChange("")}
                        className="rounded-full border border-[var(--hopp-panel-strong)] px-3 py-1 transition hover:border-[var(--hopp-sapphire)] hover:text-[var(--hopp-text)]"
                    >
                        Clear
                    </button>
                    <button
                        type="button"
                        onClick={() => onDateChange(todayKey)}
                        className="rounded-full border border-[var(--hopp-panel-strong)] px-3 py-1 transition hover:border-[var(--hopp-sapphire)] hover:text-[var(--hopp-text)]"
                    >
                        Today
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div
            ref={containerRef}
            className="absolute right-3 top-1/2 -translate-y-1/2"
        >
            <button
                type="button"
                aria-label="Pick a date"
                onClick={() => onOpenDatePicker(anchor)}
                className="flex h-8 w-8 items-center justify-center rounded-full border border-[var(--hopp-panel-strong)] bg-[var(--hopp-panel)] text-[var(--hopp-sapphire)] shadow-[0_6px_16px_rgba(20,35,90,0.35)] transition hover:border-[var(--hopp-sapphire)] hover:text-[var(--hopp-sapphire)]"
            >
                <svg
                    viewBox="0 0 24 24"
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                >
                    <rect x="3" y="4" width="18" height="18" rx="2" />
                    <path d="M16 2v4M8 2v4M3 10h18" />
                </svg>
            </button>
            {renderDateMenu()}
        </div>
    );
}
